import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Loader2, FileSpreadsheet, FileText } from 'lucide-react';
import { format, startOfYear, endOfYear } from 'date-fns';
import { toast } from 'sonner';
import { getAuthToken } from '../../lib/authToken';
import { FormateurTab } from './FormateurTab';
import { PeriodeFilter } from './PeriodeFilter';
import { saveBlob } from '../../lib/download';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function RapportFormateurPanel({ formateurId }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${getAuthToken()}` }), []);
  const [periode, setPeriode] = useState('annee');
  const [debut, setDebut] = useState(format(startOfYear(new Date()), 'yyyy-MM-dd'));
  const [fin, setFin] = useState(format(endOfYear(new Date()), 'yyyy-MM-dd'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);

  const fetchData = useCallback(async () => {
    if (!formateurId || !debut || !fin) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/rapports/formateur/${formateurId}`, { headers, params: { debut, fin } });
      setData(res.data);
    } catch {
      toast.error('Erreur lors du chargement du rapport');
    } finally {
      setLoading(false);
    }
  }, [formateurId, debut, fin, headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async (fileFormat) => {
    const base = `${API_URL}/rapports/formateur/${formateurId}`;
    const url = fileFormat === 'pdf' ? `${base}/export-pdf` : `${base}/export`;
    setExporting(fileFormat);
    try {
      const res = await axios.get(url, { headers, params: { debut, fin }, responseType: 'blob' });
      const match = (res.headers['content-disposition'] || '').match(/filename="(.+)"/);
      saveBlob(res.data, match ? match[1] : `rapport_formateur.${fileFormat === 'pdf' ? 'pdf' : 'xlsx'}`);
      toast.success(fileFormat === 'pdf' ? 'Export PDF téléchargé' : 'Export Excel téléchargé');
    } catch {
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="rapport-formateur-panel">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <PeriodeFilter periode={periode} setPeriode={setPeriode} debut={debut} setDebut={setDebut} fin={fin} setFin={setFin} showSession={false} />
        <div className="flex items-center gap-2">
          <Button type="button" size="sm" onClick={() => handleExport('xlsx')} disabled={!!exporting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="export-formateur-excel-btn">
            {exporting === 'xlsx' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1.5" />}
            Excel
          </Button>
          <Button type="button" size="sm" onClick={() => handleExport('pdf')} disabled={!!exporting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="export-formateur-pdf-btn">
            {exporting === 'pdf' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />}
            PDF
          </Button>
        </div>
      </div>
      <FormateurTab data={data} loading={loading} />
    </div>
  );
}
